import { Link } from "react-router-dom";
import { CopyButton } from "@/features/ordini/components/OrderCard";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { formatDateIt, formatMoney } from "@/shared/format";
import { Pill, Riga, Righe, type Tono } from "./parti";
import type { ContextOrder, ThreadContextResponse } from "./tipi";

const STATE_LABEL: Record<string, string> = {
  nuovo: "Nuovo",
  in_lavorazione: "In lavorazione",
  pronto: "Pronto",
  spedito: "Spedito",
  annullato: "Annullato",
};

// Stesso vocabolario dei badge in lista ordini (Davide 2026-09-01).
const PAYMENT: Record<string, { label: string; tono: Tono }> = {
  paid: { label: "Pagato", tono: "verde" },
  ordered_unpaid: { label: "Da pagare", tono: "giallo" },
  unpaid_2days: { label: "Da pagare (2+ gg)", tono: "rosso" },
  not_ordered: { label: "Non ordinato", tono: "giallo" },
  as_sent: { label: "Ordinato", tono: "verde" },
  as_to_send: { label: "Da ordinare", tono: "giallo" },
  shipped: { label: "Spedito", tono: "verde" },
  cancelled: { label: "Annullato", tono: "rosso" },
  awaiting_payment: { label: "Da incassare", tono: "giallo" },
};

const GIORNO_MS = 86_400_000;

/** «16 giorni fa»: in chat il cliente scrive «ho ordinato due settimane fa». */
function giorniFa(iso: string | null | undefined): string | null {
  const t = iso ? Date.parse(iso) : Number.NaN;
  if (!Number.isFinite(t)) return null;
  const giorni = Math.floor((Date.now() - t) / GIORNO_MS);
  if (giorni <= 0) return "oggi";
  return giorni === 1 ? "ieri" : `${giorni} giorni fa`;
}

/** Il pagamento del cliente. A ordine spedito ``payment_state`` dice
 *  «Spedito» (doppione dello stato): li' conta solo se il cliente ha pagato. */
function pagamento(order: ContextOrder): { label: string; tono: Tono } {
  if (order.payment_state === "shipped" || order.payment_state === "cancelled") {
    return order.customer_unpaid
      ? { label: "Da pagare", tono: "rosso" }
      : { label: "Pagato", tono: "verde" };
  }
  return PAYMENT[order.payment_state] ?? { label: order.payment_state, tono: "neutro" };
}

/** Stato in una pill: il corriere vince su Poleepo quando dice consegnato. */
function statoOrdine(order: ContextOrder): { label: string; tono: Tono } {
  const ev = order.last_tracking_event;
  if (ev?.ritorno_mittente) return { label: ev.label || "Ritorno al mittente", tono: "rosso" };
  if (ev?.delivered) {
    const quando = formatDateIt(ev.at);
    return { label: quando ? `Consegnato ${quando}` : "Consegnato", tono: "verde" };
  }
  if (order.state === "annullato") return { label: "Annullato", tono: "rosso" };
  if (order.state === "spedito") return { label: "Spedito", tono: "blu" };
  return { label: STATE_LABEL[order.state] || order.state, tono: "neutro" };
}

/** Scadenza di spedizione del marketplace, solo finche' l'ordine non parte:
 *  rossa quando mancano meno di 24 ore o e' passata, gialla sotto le 48. */
function Scadenza({ order }: { order: ContextOrder }) {
  if (!order.ship_deadline || order.state === "spedito" || order.state === "annullato") {
    return null;
  }
  const t = Date.parse(order.ship_deadline);
  if (!Number.isFinite(t)) return null;
  const ore = (t - Date.now()) / 3_600_000;
  const data = formatDateIt(order.ship_deadline);
  const tono: Tono = ore < 24 ? "rosso" : ore < 48 ? "giallo" : "neutro";
  return (
    <Riga label="Spedire">
      <Pill tono={tono}>{ore < 0 ? `scaduta il ${data}` : `entro il ${data}`}</Pill>
    </Riga>
  );
}

/** Testata del pannello: chi, da dove, quale ordine, quando, a che punto.
 *  Sta sopra lo scroll, cosi' resta visibile mentre si legge il resto. */
export function Testata({ data }: { data: ThreadContextResponse }) {
  const order = data.order;
  const logoSource = data.source || data.source_label || "";
  const pagato = order ? pagamento(order) : null;
  const stato = order ? statoOrdine(order) : null;
  const arrivo = order ? formatDateIt(order.created_at_poleepo) : "";
  const fa = order ? giorniFa(order.created_at_poleepo) : null;

  return (
    <header className="flex flex-col gap-2 border-b border-line bg-card px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-[12px] font-bold text-ink">
          <MarketplaceLogo source={logoSource} size={20} />
          <span className="truncate">{data.account_label || data.source_label}</span>
        </span>
        {order && (
          <span className="whitespace-nowrap font-display text-[18px] font-bold tabular-nums text-ink">
            {formatMoney(Number(order.total_amount), order.currency)}
          </span>
        )}
      </div>
      <div className="font-display text-[18px] font-bold leading-tight text-ink">
        {data.customer_name || order?.customer_name || "Cliente"}
      </div>
      {order && (
        <>
          <div className="flex min-w-0 items-center gap-1.5">
            <Link
              to={`/ordini/${encodeURIComponent(order.reference)}`}
              className="truncate tabular-nums text-[13px] font-semibold text-ink underline decoration-dotted underline-offset-2 hover:text-accent-2 hover:decoration-solid"
              title={`Apri ordine ${order.reference}`}
            >
              {order.reference}
            </Link>
            <CopyButton text={order.reference} label="Copia numero ordine" />
            <Link
              to={`/ordini/${encodeURIComponent(order.reference)}`}
              className="ml-auto shrink-0 rounded-sm border border-line-2 bg-card px-2 py-[3px] text-[11.5px] font-semibold text-ink hover:border-accent hover:bg-paper"
            >
              Apri ordine
            </Link>
          </div>
          <Righe>
            {arrivo && (
              <Riga label="Ordinato il">
                {arrivo}
                {fa && <span className="font-normal">· {fa}</span>}
              </Riga>
            )}
            <Riga label="Stato">
              {stato && <Pill tono={stato.tono}>{stato.label}</Pill>}
              {pagato && <Pill tono={pagato.tono}>{pagato.label}</Pill>}
            </Riga>
            <Scadenza order={order} />
          </Righe>
        </>
      )}
    </header>
  );
}
